import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import supabase from '../lib/supabase';
import SEO from '../components/SEO';

const DEMO_EMAIL = import.meta.env.VITE_LAUNCH_DEMO_EMAIL || 'test1@victorlazari.com';
const DEMO_PASSWORD = import.meta.env.VITE_LAUNCH_DEMO_PASSWORD || 'LaunchDemo2025!';

type DemoStatus = 'loading' | 'resetting' | 'redirecting' | 'error';

const DemoLogin: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<DemoStatus>('loading');
  const demoEmailLower = DEMO_EMAIL.toLowerCase();

  useEffect(() => {
    let cancelled = false;

    const attemptLogin = async () => {
      const { data } = await supabase.auth.getUser();
      if (cancelled) return;

      const existingUser = data?.user;
      const existingEmail = existingUser?.email?.toLowerCase();

      if (existingUser && existingEmail === demoEmailLower) {
        setStatus('resetting');
        const { error: resetError } = await supabase.rpc('reset_demo_sandbox');
        if (resetError) {
          console.warn('[demo-login] sandbox reset failed', resetError);
        }
        if (cancelled) return;
        setStatus('redirecting');
        navigate('/crm', { replace: true });
        return;
      }

      if (existingUser) {
        await supabase.auth.signOut();
      }

      if (cancelled) return;

      const { error } = await supabase.auth.signInWithPassword({ email: DEMO_EMAIL, password: DEMO_PASSWORD });
      if (cancelled) return;

      if (error) {
        console.warn('[demo-login] automatic access failed', error);
        setStatus('error');
        return;
      }

      setStatus('resetting');
      const { error: resetError } = await supabase.rpc('reset_demo_sandbox');
      if (resetError) {
        console.warn('[demo-login] sandbox reset failed', resetError);
      }

      if (cancelled) return;

      setStatus('redirecting');
      navigate('/crm', { replace: true });
    };

    attemptLogin();

    return () => {
      cancelled = true;
    };
  }, [navigate, demoEmailLower]);

  const statusMessage = (() => {
    if (status === 'loading') return t('demoLogin.signingIn');
    if (status === 'resetting') return t('demoLogin.resetting');
    if (status === 'redirecting') return t('demoLogin.successRedirect');
    return t('demoLogin.errorMessage');
  })();

  return (
    <div className="min-h-screen grid place-items-center p-6">
      <SEO
        title={t('demoLogin.title') || 'Demo Login'}
        description={t('demoLogin.description') as string}
        canonical="https://victorlazari.com/demo-login"
      />
      <div className="w-full max-w-md space-y-5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 text-center shadow-lg">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 leading-tight">{t('demoLogin.title')}</h1>
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{t('demoLogin.description')}</p>
        </div>

        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 px-4 py-3">
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{statusMessage}</p>
        </div>

        {status === 'error' && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{t('demoLogin.manualHint')}</p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center rounded-lg bg-cyan-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-cyan-600 shadow-md"
            >
              {t('demoLogin.manualCta')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoLogin;


